import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styles from './Magazines.module.scss';
import useIsRtl from '../../../../app/helpers/isRtl';
import defaultImage from '../../../../assets/images/defaultNews.png';
import { useGetQuery } from '../../../../services/apiCall';
import endpoints from '../../../../api/endpoints';
import useScrollToTop from '../../../../app/hooks/useScrollToTop';
import Text from '../../../atoms/text/Text';
import { useNavigate } from 'react-router-dom';
import Loader from '../../../atoms/loader/Loader';
import NoData from '../../../organism/filterContainer/factory/noData/noData';
import { SortMenu } from '../../newsAndEventsTemplate/newsTemplate/NewsPage';
import NewsSearchModal from '../../newsAndEventsTemplate/newsTemplate/news-search-modal/NewsSearchModal';
import BookItem from '../../../molecule/bookItem/BookItem';
import { publicationsData } from '../../../../features/academyEcoMagazine/outlets/researchAndPublications/research';
import PublicationCard from '../../../molecule/publicationCard/PublicationCard';
import getCookie from '../../../../app/helpers/getCookie';

const LIST_ENDPOINTS = {
  ecoMagazine: endpoints.ecoMagazine.getEcoPdf,
  security: endpoints.security.getSecurity,
  guideline: endpoints.guideline.getGuilines,
};

const pickList = (payload) =>
  Array.isArray(payload) ? payload : (payload?.items ?? payload?.data ?? []);

const normalizeItem = (type, item) => {
  switch (type) {
    case 'ecoMagazine':
      return {
        id: item?.magazineId ?? item?.id ?? item?.Id ?? null,
        magazineId: item?.magazineId ?? item?.id ?? item?.Id ?? null,
        title_AR: item?.title_AR ?? '',
        magazineDate: item?.magazineDate ?? null,
        url: item?.url ?? '',
        magazineThumbnail: item?.magazineThumbnail ?? '',
        magazineBlob: item?.magazineBlob ?? '',
        editionNumber: item?.editionNumber ?? null,
        pageNumber: item?.pageNumber ? Number(item.pageNumber) : null,
        type: 0,
      };
    case 'security':
    case 'guideline':
      return {
        id: item?.magazineId ?? item?.id ?? item?.Id ?? null,
        magazineId: item?.magazineId ?? item?.id ?? item?.Id ?? null,
        title_AR: item?.title_AR ?? '',
        magazineDate: item?.magazineDate ?? null,
        url: item?.url ?? '',
        magazineThumbnail: item?.magazineThumbnail ?? '',
        magazineBlob: item?.magazineBlob ?? '',
        editionNumber: item?.editionNumber ?? null,
        pageNumber: item?.pageNumber ? Number(item.pageNumber) : null,
        type: type === 'security' ? 2 : type === 'guideline' ? 3 : '',
      };
    case 'publication':
      return {
        id: `${item['Research Title EN'] ?? item['Research Title AR'] ?? Math.random()}`,
        publication: item,
        type: 4,
      };

    default:
      return {
        id: null,
        title: '',
        date: null,
        image: defaultImage,
        type,
      };
  }
};

const Section = React.memo(function Section({
  className = '',
  imageWrapper,
  title,
  items = [],
  isLoading = false,
  isRtl = false,
  to,
  sortBy = 'date',
  containerRef,
  useBookCard = true,
  redirectType = 1,
}) {
  const navigate = useNavigate();

  const handleViewMore = React.useCallback(() => {
    if (to) navigate(to);
  }, [navigate, to]);

  const itemsSorted = useMemo(() => {
    const list = Array.isArray(items) ? items : [];

    const getTitleVal = (it) => {
      if (it?.type === 4) {
        return (
          it.publication?.['Research Title AR'] ??
          it.publication?.['Research Title EN'] ??
          ''
        );
      }
      return it?.title_AR ?? it?.title ?? '';
    };

    const getDateVal = (it) => {
      if (it?.type === 4) {
        const y = it.publication?.Year;
        return Number.isFinite(y) ? new Date(`${y}-12-31`).getTime() : NaN;
      }
      const d = it?.magazineDate ?? it?.date ?? null;
      return d ? new Date(d).getTime() : NaN;
    };

    const sorted = [...list].sort((a, b) => {
      if (sortBy === 'title') {
        const av = getTitleVal(a);
        const bv = getTitleVal(b);
        return av.localeCompare(bv, undefined, { sensitivity: 'base' });
      }
      const ad = getDateVal(a);
      const bd = getDateVal(b);
      if (Number.isNaN(ad) && Number.isNaN(bd)) return 0;
      if (Number.isNaN(ad)) return 1;
      if (Number.isNaN(bd)) return -1;
      return bd - ad;
    });

    return sorted;
  }, [items, sortBy]);

  return (
    <section className={styles.section}>
      <div className={`${styles.sectionHeader} ${isRtl ? styles.rtl : ''}`}>
        <Text
          title={title}
          variant="text-bold-20"
          color="black"
          className={styles.text}
        />
        {to && (
          <button type="button" onClick={handleViewMore}>
            <Text
              title="common.view_all"
              variant="text-bold-italic-15"
              color="black"
              className={styles.text}
            />
          </button>
        )}
      </div>

      {isLoading ? (
        <div style={{ width: '100%', height: '50vh' }}>
          <Loader />
        </div>
      ) : items.length === 0 ? (
        <div className={styles.empty}>
          <NoData />
        </div>
      ) : (
        <div
          className={`${styles.grid} ${isRtl ? styles.rtl : ''}`}
          ref={containerRef}
        >
          {itemsSorted.slice(0, 4).map((item) => (
            <div
              key={`${item?.type ?? 'item'}-${item?.id}`}
              className={styles.searchable}
              data-search-item
              data-date={
                item?.type === 4
                  ? (item.publication?.Year ?? '')
                  : item.magazineDate || item.date || ''
              }
              data-type={item.type}
            >
              <BookItem
                book={item}
                redirectType={redirectType}
                cardImageClass={imageWrapper}
              />
            </div>
          ))}
        </div>
      )}
    </section>
  );
});

const Magazines = () => {
  const navigate = useNavigate();

  useScrollToTop();
  const isRtl = useIsRtl();
  const [sortBy, setSortBy] = useState('date');
  const [isSortOpen, setIsSortOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  const ecoRef = useRef(null);
  const secRef = useRef(null);
  const guideRef = useRef(null);
  const searchRefs = useMemo(() => [ecoRef, secRef, guideRef], []);
  const {
    data: ecoData,
    refetch: refetchEco,
    isLoading: ecoLoading,
  } = useGetQuery({
    apiUrl: LIST_ENDPOINTS.ecoMagazine,
    params: ['1000', '1'],
  });

  const {
    data: securityData,
    refetch: refetchSecurity,
    isLoading: securityLoading,
  } = useGetQuery({
    apiUrl: LIST_ENDPOINTS.security,
    params: ['1000', '1'],
  });

  const {
    data: guidelineData,
    refetch: refetchGuideline,
    isLoading: guidelineLoading,
  } = useGetQuery({
    apiUrl: LIST_ENDPOINTS.guideline,
    params: ['1000', '1'],
  });
  const publicationItems = useMemo(
    () =>
      (Array.isArray(publicationsData) ? publicationsData : [])
        .map((pub) => normalizeItem('publication', pub))
        .filter((x) => x.type === 4)
        .slice(0, 3),
    [publicationsData]
  );
  const ecoItems = useMemo(
    () =>
      pickList(ecoData)
        .map((n) => normalizeItem('ecoMagazine', n))
        .filter((x) => x.id),
    [ecoData]
  );

  const securityItems = useMemo(
    () =>
      pickList(securityData)
        .map((n) => normalizeItem('security', n))
        .filter((x) => x.id),
    [securityData]
  );

  const guidelineItems = useMemo(
    () =>
      pickList(guidelineData)
        .map((n) => normalizeItem('guideline', n))
        .filter((x) => x.id),
    [guidelineData]
  );

  const onToggleSort = useCallback((next) => setIsSortOpen(Boolean(next)), []);
  const openSearchStable = useCallback(() => setIsSearchOpen(true), []);
  const closeSearch = useCallback(() => setIsSearchOpen(false), []);
  const [isEnglish, setIsEnglish] = useState(false);

  useEffect(() => {
    const cookieVal = getCookie('googtrans');
    const isTranslatedToEnglish =
      cookieVal?.endsWith('/ar/en') || cookieVal?.endsWith('/auto/en');
    if (isTranslatedToEnglish) {
      setIsEnglish(true);
    }
  }, []);

  const title = isEnglish
    ? 'Policies and Guidelines'
    : 'guideline.title_plural';

  return (
    <div className={styles.mediaCenter}>
      <div className={styles['header-title']}>
        <Text
          title="common.Magazines"
          variant="text-bold-italic-30"
          color="black"
        />
      </div>

      <div>
        <SortMenu
          isRtl={isRtl}
          isOpen={isSortOpen}
          onToggle={onToggleSort}
          sortBy={sortBy}
          onPick={setSortBy}
          setIsSearchOpen={openSearchStable}
        />

        <div className={styles.sections}>
          <Section
            title="Magazine.Academy_Echo_Magazine"
            items={ecoItems}
            isLoading={ecoLoading}
            imageWrapper={styles['image-news']}
            isRtl={isRtl}
            to="/academy-eco-magazine"
            sortBy={sortBy}
            containerRef={ecoRef}
            useBookCard
            redirectType={1}
          />

          <Section
            title="security_and_law.header_title"
            items={securityItems}
            isLoading={securityLoading}
            imageWrapper={styles['image-news']}
            isRtl={isRtl}
            to="/security-and-law"
            sortBy={sortBy}
            containerRef={secRef}
            useBookCard
            redirectType={1}
          />

          <Section
            title={title}
            items={guidelineItems}
            isLoading={guidelineLoading}
            imageWrapper={styles['image-news']}
            isRtl={isRtl}
            to="/research/guideline"
            sortBy={sortBy}
            containerRef={guideRef}
            useBookCard
            redirectType={1}
          />
          <div className={styles.publicationSection}>
            <div
              className={`${styles.sectionHeader} `}
              style={{ flexDirection: isRtl ? 'row' : 'row-reverse' }}
            >
              <button
                type="button"
                onClick={() => navigate('/academic-research-and-publications')}
              >
                <Text
                  title="common.view_all"
                  variant="text-bold-italic-15"
                  color="black"
                  className={styles.text}
                />
              </button>
              <Text
                title="academic_research_and_publications.header_title"
                variant="text-bold-20"
                color="black"
                className={styles.text}
              />
            </div>

            <div
              className={styles['publication-grid']}
              dir={isRtl ? 'rtl' : 'ltr'}
            >
              {publicationItems.map((item, idx) => (
                <PublicationCard
                  key={`pub-${idx}`}
                  publication={item.publication}
                  currentLanguage="ar"
                  onClick={() =>
                    console.log('Clicked publication', item.publication)
                  }
                />
              ))}
            </div>
          </div>
        </div>

        <NewsSearchModal
          isOpen={isSearchOpen}
          onClose={closeSearch}
          containerRefs={searchRefs}
        />
      </div>
    </div>
  );
};

export default Magazines;
